// Typed API client — openapi-fetch bound to the generated schema. DESIGN.md §3, §4.3.1, §8.1.1,
// §9.
//
// `./schema.d.ts` is generated from `docs/design/api-contract.yaml` by `npm run gen:api`
// (openapi-typescript, version pinned in package.json) and committed; `make verify-slow` fails
// the moment it drifts from the contract. This file adds no path, no method and no field of its
// own — the contract is the only source of truth (CLAUDE.md "the API contract is law").
import createClient from 'openapi-fetch';
import type { Middleware } from 'openapi-fetch';
import { signInPathFor } from '../../features/auth/doors';
import type { paths } from './schema';

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const CSRF_COOKIE_NAME = 'urunrumah_csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

/**
 * Same-origin, in every environment (DESIGN.md §4.3.1). `NEXT_PUBLIC_API_BASE_URL` is a
 * root-relative path, `/v1`, in every environment — dev and CI reach the backend through
 * `next.config.mjs`'s rewrite, pilot through Caddy, and the browser never calls a different
 * origin. An absolute URL here would silently make every call cross-origin, so one is refused at
 * module load rather than sent. `NEXT_PUBLIC_*` variables are inlined at build time wherever they
 * are read, client or server, so this check runs unconditionally — not only in the browser
 * branch of {@link resolveApiBaseUrl}.
 */
export function assertRootRelativeApiBaseUrl(value: string): string {
  if (!value.startsWith('/') || value.startsWith('//')) {
    throw new Error(
      'lib/api/client.ts: NEXT_PUBLIC_API_BASE_URL must be a root-relative path starting with a ' +
        `single "/" (DESIGN.md §4.3.1) — the browser reaches the API same-origin only; got ${JSON.stringify(value)}.`,
    );
  }
  return value;
}

const PUBLIC_API_BASE_URL = assertRootRelativeApiBaseUrl(
  process.env.NEXT_PUBLIC_API_BASE_URL ?? '/v1',
);

// The dev/CI default `next.config.mjs` also uses when `API_INTERNAL_URL` is unset
// (`src/lib/config/apiInternalUrl.mjs`). By the time a production build or `next start` is
// actually running, that file has already refused to load without a real value, so this fallback
// only ever fires in `next dev` without the variable set, or a broken deploy — either way it
// fails loudly against a real, named host rather than silently against a guessed one.
const SERVER_API_BASE_URL_FALLBACK = 'http://localhost:3000/v1';

/**
 * Chooses the base URL by environment (DESIGN.md §4.3.1): a browser request goes root-relative,
 * same-origin, through `/v1`; server-side code (Server Components, route handlers, Server
 * Actions) uses the absolute `API_INTERNAL_URL`, because a relative base cannot work outside a
 * browser, and never forwards the visitor's session cookie — a page that needs the session reads
 * it from the browser instead. Takes `isBrowser` explicitly, rather than reading `typeof window`
 * itself, so both branches are unit-testable without faking global `window`.
 */
export function resolveApiBaseUrl(
  isBrowser: boolean,
  env: { API_INTERNAL_URL?: string; [key: string]: unknown } = process.env,
): string {
  if (isBrowser) {
    return PUBLIC_API_BASE_URL;
  }
  // Same emptiness rule as `src/lib/config/apiInternalUrl.mjs`: unset, empty and whitespace-only
  // all mean "not set" (F-16, docs/qa/reports/T-03-r2.md). `??` alone would take `""` as a value
  // and hand server-side code a relative base URL that cannot work outside a browser.
  const internal = typeof env.API_INTERNAL_URL === 'string' ? env.API_INTERNAL_URL.trim() : '';
  return internal || SERVER_API_BASE_URL_FALLBACK;
}

/**
 * Reads `urunrumah_csrf` fresh from `document.cookie` at the moment of the request (DESIGN.md
 * §8.1.1: "Never cache it — a login in another tab must be picked up"). Returns `undefined`,
 * never throws, when there is no `document` (server-side) or the cookie is not present.
 *
 * The value is returned exactly as `document.cookie` presents it: no `decodeURIComponent`, no
 * trimming, no other transformation. §8.1.1's Header row requires the header to carry the cookie
 * value byte for byte, and the server compares the two for equality. A legitimate token
 * (`^[A-Za-z0-9_-]{43}$`) contains no `%`, so decoding could only ever change a value the server
 * will refuse anyway. It could also throw `URIError` on a malformed escape planted from a sibling
 * subdomain or a plain-HTTP page, which would block every unsafe request, `auth_login` (the
 * recovery path) included (F-11, docs/qa/reports/T-03-r2.md). Only the cookie *name* is trimmed,
 * to drop the space `document.cookie` puts after each `;`.
 */
export function readCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const pair of document.cookie.split(';')) {
    const separatorIndex = pair.indexOf('=');
    if (separatorIndex === -1) {
      continue;
    }
    if (pair.slice(0, separatorIndex).trim() === CSRF_COOKIE_NAME) {
      return pair.slice(separatorIndex + 1);
    }
  }
  return undefined;
}

/**
 * DESIGN.md §8.1.1 "Frontend half": on `POST`, `PUT`, `PATCH`, `DELETE` only, set `X-CSRF-Token`
 * to the `urunrumah_csrf` cookie's value at the moment of the request. `GET`, `HEAD` and
 * `OPTIONS` never carry it. When the cookie is absent (or there is no `document`), the request is
 * sent unchanged — the server's answer is authoritative, per the same section. The value is read
 * fresh on every request and never written to `localStorage`, `sessionStorage`, a log or an error
 * report; it exists only on the `Request` this function returns.
 *
 * This function never throws. A cookie value that no HTTP header can carry (a character above
 * U+00FF, or a control character) makes `Headers.set` throw a `TypeError`. In that case the
 * request is sent unchanged, exactly as if the cookie were absent, and the server answers. A
 * browser will not store such a value from a `Set-Cookie` the backend sends, but page script on a
 * sibling origin can plant one.
 */
export const csrfMiddleware: Middleware = {
  onRequest({ request }) {
    if (!UNSAFE_METHODS.has(request.method)) {
      return undefined;
    }
    const token = readCsrfCookie();
    if (!token) {
      return undefined;
    }
    try {
      request.headers.set(CSRF_HEADER_NAME, token);
    } catch {
      return undefined;
    }
    return request;
  },
};

// Endpoints that legitimately answer 401 `authentication_required` to a visitor who was never
// signed in (`/me` is how a page learns whether anyone is), and the pages a visitor lands on to
// sign in. Neither is a session that *ended*, so neither is redirected.
const SESSION_PROBE_PATH = /\/(me|auth\/[^?]*)(\?|$)/;
// Matches both doors — `/masuk` and `/masuk/staf` — and `/daftar`; verified, not changed, by
// CR-007 §4.6 (frontend/src/lib/api/sessionExpired.test.ts).
const AUTH_PAGE_PATH = /^\/(masuk|daftar)(\/|$)/;

/**
 * A staff session idles out after 30 minutes and every session is revocable, so any request can
 * come back `401 authentication_required` on a page that rendered fine a moment ago. Without this
 * each list shows its own generic "could not load" notice and the visitor never learns why. This
 * sends the browser to `<signInPathFor(pathname)>?sesi=berakhir&lanjut=<where they were>` — the
 * investor or staff door, picked the same way `middleware.ts` picks it (CR-007 §4.6) — where the
 * door's panel says the session ended and returns them there after sign-in. Browser-only; reads a
 * clone so the caller still gets the untouched response.
 */
export const sessionExpiredMiddleware: Middleware = {
  async onResponse({ request, response }) {
    if (typeof window === 'undefined' || response.status !== 401) {
      return undefined;
    }
    const { pathname, search } = window.location;
    if (SESSION_PROBE_PATH.test(new URL(request.url, window.location.href).pathname) || AUTH_PAGE_PATH.test(pathname)) {
      return undefined;
    }
    try {
      const body = (await response.clone().json()) as { error?: { code?: string } };
      if (body.error?.code !== 'authentication_required') {
        return undefined;
      }
    } catch {
      return undefined;
    }
    // A full document navigation, deliberately not `router.push`: this middleware lives outside
    // React (no router in scope), and the reload discards every client cache (TanStack Query data,
    // in-memory state) that belonged to the session that just ended. `signInPathFor` picks the
    // door by the page's own pathname (CR-007 §4.6) — never a literal `/masuk`.
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- full reload drops all client cache with the dead session
    window.location.assign(
      `${signInPathFor(pathname)}?sesi=berakhir&lanjut=${encodeURIComponent(`${pathname}${search}`)}`,
    );
    return undefined;
  },
};

/**
 * The one client every feature slice imports — `api.GET(path, …)`, `api.POST(path, …)`, fully
 * typed against `paths`. `credentials: 'same-origin'` sends the httpOnly `urunrumah_session`
 * cookie (DESIGN.md §8.1, api-contract.yaml `securitySchemes.sessionCookie`) only to same-origin
 * requests — never cross-origin, matching §4.3.1's same-origin design. The server is the sole
 * authorization boundary (DESIGN.md §8.5): this client makes no authorization decision of its
 * own, matching `middleware.ts`'s cookie-presence-only check. `csrfMiddleware` is registered once
 * here, at module scope, so every request through this one instance carries the header when one
 * applies — coverage does not depend on which feature slice a route tree imports (DESIGN.md
 * §8.1.1 "Frontend half", point 1).
 */
export const api = createClient<paths>({
  baseUrl: resolveApiBaseUrl(typeof window !== 'undefined'),
  credentials: 'same-origin',
});

api.use(csrfMiddleware);
api.use(sessionExpiredMiddleware);

export type { components, operations, paths } from './schema';
